import React, { useMemo, useState } from 'react';

export type SpadeOverview = {
  total: number;
  installed: number;
  warehouse: number;
  personnel: number;
  need_repair: number;
  dropped: number;
};

type Props = {
  colors: any;
  data: SpadeOverview;
};

// Helpers for Persian digits & separators
const faNum = (v: number) => v.toLocaleString('fa-IR');
const faTxt = (s: string | number) =>
  String(s)
    .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    .replace(/\./g, '٫')
    .replace(/,/g, '٬');

const SpadesOverviewCard: React.FC<Props> = ({ colors, data }) => {
  const items = useMemo(
    () => [
      { key: 'installed',   label: 'نصب‌شده',        color: '#34d399', value: data.installed ?? 0 },
      { key: 'warehouse',   label: 'در انبار',        color: '#60a5fa', value: data.warehouse ?? 0 },
      { key: 'personnel',   label: 'در اختیار پرسنل', color: '#fbbf24', value: data.personnel ?? 0 },
      { key: 'need_repair', label: 'نیازمند تعمیر',   color: '#fb923c', value: data.need_repair ?? 0 },
      { key: 'dropped',     label: 'اسقاط‌شده',       color: '#f87171', value: data.dropped ?? 0 },
    ],
    [data]
  );

  const total =
    data.total && data.total > 0
      ? data.total
      : Math.max(0, items.reduce((s, i) => s + (i.value || 0), 0));

  const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0);
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  // گرادیان کامل
  let acc = 0;
  const fullParts: string[] = [];
  items.forEach((it) => {
    if (it.value <= 0) return;
    const angle = (it.value / (total || 1)) * 360;
    const start = acc;
    const end = acc + angle;
    fullParts.push(`${it.color} ${start}deg ${end}deg`);
    acc = end;
  });
  const fullGradient =
    fullParts.length > 0
      ? `conic-gradient(${fullParts.join(', ')})`
      : `conic-gradient(#1f2937 0deg 360deg)`;

  // گرادیان فوکوس
  let focusGradient = '';
  if (hoverKey) {
    acc = 0;
    for (const it of items) {
      const angle = (it.value / (total || 1)) * 360;
      const start = acc;
      const end = acc + angle;
      if (it.key === hoverKey) {
        focusGradient = `conic-gradient(
          transparent ${start}deg,
          ${it.color} ${start}deg ${end}deg,
          transparent ${end}deg
        )`;
        break;
      }
      acc = end;
    }
  }

  const hoverItem = items.find((i) => i.key === hoverKey) || null;

  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 6px 20px rgba(0,0,0,.25)',
    display: 'grid',
    gap: 14,
  };

  const cardTitle: React.CSSProperties = {
    fontWeight: 800,
    letterSpacing: 0.2,
    fontSize: 14,
  };

  // ⬇️ Single-column stack: donut on top, legend under it
  const donutWrap: React.CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto auto',
    justifyItems: 'center',
    alignItems: 'start',
    gap: 12,
    marginTop: -20, // lift toward the top; tweak as you like
  };

  const donut: React.CSSProperties = {
    width: 240,
    height: 240,
    borderRadius: '50%',
    background: hoverKey ? focusGradient : fullGradient,
    display: 'grid',
    placeItems: 'center',
    boxShadow: 'inset 0 0 20px rgba(0,0,0,.35)',
    transition: 'background .25s ease',
  };

  const hole: React.CSSProperties = {
    width: 160,
    height: 160,
    borderRadius: '50%',
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    display: 'grid',
    placeItems: 'center',
    textAlign: 'center',
    padding: 8,
  };

  const legendCol: React.CSSProperties = {
    display: 'grid',
    gap: 7,
    width: '100%',
    maxWidth: 420,     // keep buttons from stretching too wide
  };

  const legendItem = (active: boolean): React.CSSProperties => ({
    border: `1px solid ${active ? colors.accent : colors.border}`,
    background: active ? 'rgba(96,165,250,0.10)' : 'rgba(255,255,255,0.02)',
    borderRadius: 12,
    padding: '7px 9px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 160,
    fontSize: 13,
    transition: 'background .2s ease, border-color .2s ease',
  });

  return (
    <section style={card} dir="rtl">
      <div style={cardTitle}>آمار کلی اسپیدها</div>

      <div style={donutWrap}>
        {/* Donut (top) */}
        <div style={donut} title="تقسیم‌بندی وضعیت اسپیدها">
          <div style={hole}>
            {hoverItem ? (
              <>
                <div style={{ fontSize: 11, color: colors.sub }}>{hoverItem.label}</div>
                <div style={{ fontSize: 32, fontWeight: 900 }}>{faNum(hoverItem.value)}</div>
                <div style={{ fontSize: 11, color: colors.sub }}>
                  {faNum(pct(hoverItem.value))}٪ از کل
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 11, color: colors.sub }}>تعداد کل اسپیدها</div>
                <div style={{ fontSize: 36, fontWeight: 900 }}>{faNum(total)}</div>
                <div style={{ fontSize: 10.5, color: colors.sub || 'black' }}>
                  برای دیدن جزئیات هر بخش، موس را ببرید
                </div>
              </>
            )}
          </div>
        </div>

        {/* Legend (under donut) */}
        <div style={legendCol}>
          {items.map((it) => (
            <div
              key={it.key}
              onMouseEnter={() => setHoverKey(it.key)}
              onMouseLeave={() => setHoverKey(null)}
              title={`${it.label}: ${faTxt(it.value)} (${faTxt(pct(it.value))}٪)`}
              style={legendItem(hoverKey === it.key)}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 999,
                  background: it.color,
                  border: `1px solid ${colors.border}`,
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>{it.label}</span>
                <span style={{ color: colors.sub, fontSize: 11 }}>
                  {faNum(it.value)} • {faNum(pct(it.value))}٪
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SpadesOverviewCard;
